import { createContext, Dispatch, SetStateAction } from 'react';
import { ConversationMessageDto, ToolInvocation } from '@endion/types';

export const ChatContext = createContext<{
  storedConversationHistory: ConversationMessageDto[];
  isOpen: boolean;
  setMessages: Dispatch<SetStateAction<ConversationMessageDto[]>>;
  messages: ConversationMessageDto[];
  updateToolInvocationResult: <TToolInvocationName extends ToolInvocation['name']>(
    _: TToolInvocationName,
    messageId: string,
    toolCallId: string,
    toolResult: Extract<ToolInvocation, { name: TToolInvocationName }>['result'],
  ) => ConversationMessageDto[];
  submit: (messages: ConversationMessageDto[]) => Promise<void>;
} | null>(null);

export function ChatContextProvider({
  children,
  storedConversationHistory = [],
  setMessages,
  isOpen,
  messages,
  submit,
}: {
  children: React.ReactNode;
  storedConversationHistory: ConversationMessageDto[];
  setMessages: Dispatch<SetStateAction<ConversationMessageDto[]>>;
  isOpen: boolean;
  messages: ConversationMessageDto[];
  submit: (messages: ConversationMessageDto[]) => Promise<void>;
}) {
  function updateToolInvocationResult<TToolInvocationName extends ToolInvocation['name']>(
    _: TToolInvocationName,
    messageId: string,
    toolCallId: string,
    toolResult: Extract<ToolInvocation, { name: TToolInvocationName }>['result'],
  ) {
    const messageToUpdate = messages.find((message) => message.id === messageId);

    if (!messageToUpdate) {
      throw new Error(`Could not find message with id ${messageId}`);
    }
    if (messageToUpdate.role !== 'assistant' || !messageToUpdate.toolInvocations) {
      throw new Error(`Message with id ${messageId} is not an assistant message or does not have tool invocations`);
    }
    const updatedMessage = messageToUpdate.toolInvocations.map((toolInvocation) => {
      if (toolInvocation.toolCallId === toolCallId) {
        return {
          ...toolInvocation,
          result: JSON.stringify(toolResult),
        };
      }
      return toolInvocation;
    });
    const updatedMessages = messages.map((message) => {
      if (message.id === messageId) {
        return {
          ...message,
          toolInvocations: updatedMessage,
        };
      }
      return message;
    });
    setMessages(updatedMessages);
    return updatedMessages;
  }
  return (
    <ChatContext.Provider
      value={{
        storedConversationHistory,
        isOpen,
        setMessages,
        messages,
        updateToolInvocationResult,
        submit,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}
